import React from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Calculator, 
  Search, 
  PieChart, 
  TrendingUp, 
  Shield, 
  Award, 
  Users, 
  ArrowRight,
  CheckCircle,
  Star,
  BarChart3,
  Target,
  Wallet
} from "lucide-react";

export const LandingPage = () => {
  const features = [
    {
      icon: Calculator,
      title: "Smart Calculators",
      description: "SIP, Lumpsum, SWP & Goal Planning calculators to plan your investments precisely."
    },
    {
      icon: Search,
      title: "Fund Research",
      description: "Deep dive into 10,000+ mutual funds with real-time NAV data from AMFI."
    },
    {
      icon: BarChart3,
      title: "Compare Funds",
      description: "Side-by-side comparison of funds to make informed investment decisions."
    },
    {
      icon: PieChart,
      title: "Portfolio Tracker",
      description: "Track all your investments in one place with real-time valuations."
    },
    {
      icon: Target,
      title: "Goal Planning",
      description: "Set financial goals and get personalized investment recommendations."
    },
    {
      icon: Wallet,
      title: "Tax Planning",
      description: "Optimize your taxes with ELSS funds and smart tax-saving strategies."
    }
  ];

  const stats = [
    { value: "10,000+", label: "Mutual Funds" },
    { value: "₹500Cr+", label: "Assets Advised" },
    { value: "50,000+", label: "Happy Clients" },
    { value: "15+", label: "Years Experience" }
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      role: "Business Owner",
      content: "Paisa Secure helped me plan my retirement systematically. Their calculators are incredibly accurate and easy to use.",
      rating: 5
    },
    {
      name: "Priya Sharma",
      role: "IT Professional",
      content: "The fund comparison tool saved me hours of research. I found the perfect ELSS fund for tax saving within minutes.",
      rating: 5
    },
    {
      name: "Amit Patel",
      role: "Doctor",
      content: "Professional advisory with a personal touch. My portfolio has grown 40% in just 2 years with their guidance.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-background grain" data-testid="landing-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24" data-testid="hero-section">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1722170585132-394fd5518008?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBza3lzY3JhcGVyJTIwbmlnaHR8ZW58MHx8fHwxNzczNzIwNTM0fDA&ixlib=rb-4.1.0&q=85"
            alt="Financial District"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/80 to-background"></div>
        </div>

        {/* Hero Glow */}
        <div className="absolute inset-0 hero-glow"></div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-8">
            <Shield className="w-4 h-4" />
            SEBI Registered | AMFI Certified
          </div>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight mb-6">
            <span className="text-foreground">Secure Your </span>
            <span className="gold-text">Financial Future</span>
          </h1>

          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            Expert mutual fund advisory backed by data-driven insights. Build wealth systematically with personalized investment strategies designed for your goals.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button className="btn-primary text-base px-8 py-6 rounded-full" data-testid="hero-get-started">
                Start Investing
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/calculators">
              <Button variant="outline" className="text-base px-8 py-6 rounded-full border-white/10 hover:bg-secondary" data-testid="hero-calculators">
                <Calculator className="w-5 h-5 mr-2" />
                Try Calculators
              </Button>
            </Link>
          </div>
          
          {/* Investor Portal Link */}
          <div className="mt-6">
            <Link to="/investor/login" className="text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="investor-portal-link">
              Already invested? <span className="text-primary font-medium">Access Investor Portal →</span>
            </Link>
          </div>

          {/* Trust Badges */}
          <div className="mt-16 flex flex-wrap items-center justify-center gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-mono text-2xl sm:text-3xl font-bold text-primary">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-primary rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 px-6" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Everything You Need to <span className="gold-text">Invest Wisely</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Comprehensive tools and insights to make informed investment decisions
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={feature.title}
                  className={`glass-card card-hover ${
                    index === 0 || index === 3 ? "lg:col-span-2" : ""
                  }`}
                  data-testid={`feature-card-${index}`}
                >
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-serif text-xl font-semibold mb-3">{feature.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 px-6 bg-card/30" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                Why Choose <span className="gold-text">Paisa Secure?</span>
              </h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                We combine cutting-edge technology with decades of investment expertise to help you achieve your financial goals faster and smarter.
              </p>

              <div className="space-y-6">
                {[
                  { id: "real-time", title: "Real-time AMFI Data", desc: "Access live NAV data for 10,000+ mutual funds" },
                  { id: "advisory", title: "Personalized Advisory", desc: "Get investment recommendations tailored to your goals" },
                  { id: "transparent", title: "Zero Hidden Fees", desc: "Transparent pricing with no hidden charges" },
                  { id: "research", title: "Expert Research Team", desc: "In-house analysts tracking market movements 24/7" }
                ].map((item) => (
                  <div key={item.id} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">{item.title}</h4>
                      <p className="text-muted-foreground text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/contact" className="inline-block mt-10">
                <Button className="btn-primary rounded-full px-8" data-testid="contact-advisor-btn">
                  Talk to an Advisor
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzczNzIwNTM1fDA&ixlib=rb-4.1.0&q=85"
                alt="Advisory Meeting"
                className="rounded-2xl shadow-2xl"
              />
              {/* Floating Card */}
              <Card className="absolute -bottom-8 -left-8 glass-card shadow-2xl max-w-xs">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <Award className="w-10 h-10 text-primary" />
                    <div>
                      <p className="font-semibold">Award Winning</p>
                      <p className="text-sm text-muted-foreground">Best MF Advisor 2024</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 px-6" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Trusted by <span className="gold-text">50,000+ Investors</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              See what our clients say about their investment journey with us
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.name} className="glass-card" data-testid={`testimonial-${testimonial.name}`}>
                <CardContent className="p-8">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-5 h-5 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-6 leading-relaxed">"{testimonial.content}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <Card className="glass-card overflow-hidden">
            <CardContent className="p-12 relative">
              <div className="absolute inset-0 hero-glow opacity-50"></div>
              <div className="relative z-10">
                <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
                  Ready to Start Your <span className="gold-text">Wealth Journey?</span>
                </h2>
                <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
                  Join thousands of investors who trust Paisa Secure for their financial growth. Start with just ₹500/month.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/register">
                    <Button className="btn-primary text-base px-8 py-6 rounded-full" data-testid="cta-get-started">
                      Create Free Account
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/calculators">
                    <Button variant="outline" className="text-base px-8 py-6 rounded-full border-white/10" data-testid="cta-calculators">
                      Explore Calculators
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};
